import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useColorSwatchDrag } from './hooks/use-color-swatch-drag'
import { ChevronDown, GripVertical, Search, X } from 'lucide-react'
import { useDocumentLayout } from '@/contexts/document-layout-context'
import { useColorsQuery } from '@/api/hooks/use-colors-query'
import { useColorStylesQuery } from '@/api/hooks/use-color-styles-query'
import { useSpreadsQuery } from '@/api/hooks/use-spreads'
import type { WireColor } from '@/api/types/color'
import { NONE_COLOR, isNoneColor } from '@/lib/colors/fill-paint'
import { cn } from '@/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InputGroup, InputGroupPrefix } from '@/components/ui/input-group'
import { Slider } from '@/components/ui/slider'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import colorNoneSvg from '@/assets/color-none.svg'
import { type ColorFamily, filterByFamily } from '@/lib/colors/classify-family'
import { tintedPaletteHex } from '@/lib/colors/resolve-palette-color'
import { type RecentColor, useRecentlyUsedColors } from './hooks/use-recently-used-colors'
import { useListResize } from './hooks/use-list-resize'
import { useFitListHeight } from './hooks/use-fit-list-height'
import { ColorSwatch } from './components/color-swatch'
import { ColorPopoverToolbar } from './components/color-popover-toolbar'
import { ColorPopoverColorList } from './components/color-popover-color-list'
import { ColorPopoverResizeGrip } from './components/color-popover-resize-grip'

interface ColorPopoverProps {
  target: 'text' | 'fill' | 'stroke'
  activeColorId: number | null
  initialTint?: number
  /** The selected objects have different tints: the field shows Mixed until a tint is set. */
  isTintMixed?: boolean
  onClose: () => void
  onColorSelect?: (color: WireColor, tint: number, target: 'text' | 'fill' | 'stroke') => void
  /** Live tint while the slider moves. */
  onTintChange?: (tint: number) => void
  /** Final tint: slider release, or a typed value. */
  onTintCommit?: (tint: number) => void
  /** Grabber drag (see `usePopoverDrag`); the grabber renders only when these are supplied. */
  isDragging?: boolean
  onDragStart?: (e: React.PointerEvent<HTMLButtonElement>) => void
  onDragMove?: (e: React.PointerEvent<HTMLButtonElement>) => void
  onDragEnd?: (e: React.PointerEvent<HTMLButtonElement>) => void
  /** Color list height, held by the host so it carries across Text, Fill and Stroke. */
  listHeight?: number
  onListHeightChange?: (height: number) => void
}

export function ColorPopover({
  target,
  activeColorId,
  initialTint,
  isTintMixed = false,
  onClose,
  onColorSelect,
  onTintChange,
  onTintCommit,
  isDragging = false,
  onDragStart,
  onDragMove,
  onDragEnd,
  listHeight: listHeightProp,
  onListHeightChange,
}: ColorPopoverProps) {
  const { spreadId } = useDocumentLayout()
  const { data: allColors = [] } = useColorsQuery()
  const { data: colorStyles = [] } = useColorStylesQuery()
  const { data: spreads = [] } = useSpreadsQuery()
  const { recentColors, addRecentColor } = useRecentlyUsedColors(allColors)

  const [selectedPageNumber, setSelectedPageNumber] = useState<number | null>(null)
  const [allColorsOpen, setAllColorsOpen] = useState(false)
  const [colorLayout, setColorLayout] = useState<'grid' | 'list'>('grid')
  const [colorFamily, setColorFamily] = useState<ColorFamily>('All families')
  const [internalColorId, setInternalColorId] = useState<number | null>(activeColorId)
  const [tint, setTint] = useState(initialTint ?? 100)
  const [tintMixed, setTintMixed] = useState(isTintMixed)
  // A typed tint not yet committed; blur commits only then, so a blur after Enter (or after an
  // undo) doesn't record a second step.
  const tintTypedRef = useRef(false)
  const { listRef, listHeight, isResizing, handleResizeStart, handleResizeMove, handleResizeEnd } =
    useListResize({ listHeight: listHeightProp, onListHeightChange })
  // A short window shrinks the list (back to the dragged height once there's room again) so the
  // popover never has to climb over the menu bar to stay on screen.
  const [popoverRootRef, maxFittingHeight] = useFitListHeight(listRef)
  const renderedListHeight = Math.min(listHeight, maxFittingHeight)

  useEffect(() => {
    setInternalColorId(activeColorId)
  }, [activeColorId])

  useEffect(() => {
    setTint(initialTint ?? 100)
    setTintMixed(isTintMixed)
  }, [initialTint, isTintMixed])

  const [searchQuery, setSearchQuery] = useState('')

  const currentSpread = spreads.find((s) => s.spreadId === spreadId) ?? null
  const pages = [...(currentSpread?.pages ?? [])].sort((a, b) => a.number - b.number)

  const effectivePageNumber = selectedPageNumber ?? pages[0]?.number ?? null
  const selectedPage = pages.find((p) => p.number === effectivePageNumber) ?? pages[0] ?? null

  const selectedStyle =
    selectedPage?.colorStyleId != null
      ? (colorStyles.find((s) => s.colorStyleId === selectedPage.colorStyleId) ?? null)
      : null

  const blackColor = allColors.find((c) => c.colorName === 'Black') ?? null
  const whiteColor = allColors.find((c) => c.colorName === 'White') ?? null

  const sortedAllColors = filterByFamily(
    [...allColors].sort((a, b) => a.pageEditorSort - b.pageEditorSort),
    colorFamily
  )
  const isRecentSelected = (recent: RecentColor) =>
    recent.color.colorId === internalColorId && recent.tint === tint
  const recentRow1 = recentColors.slice(0, 5)
  const recentRow2 = recentColors.slice(5, 10)

  const internalColor =
    internalColorId != null ? (allColors.find((c) => c.colorId === internalColorId) ?? null) : null

  const styleColorIds = new Set(selectedStyle?.colors.map((c) => c.colorId) ?? [])

  const searchResults =
    searchQuery.trim().length > 0
      ? filterByFamily(
          allColors.filter((c) =>
            c.colorName.toLowerCase().includes(searchQuery.trim().toLowerCase())
          ),
          colorFamily
        )
      : null

  const headerSwatchDrag = useColorSwatchDrag(internalColor ?? NONE_COLOR, tint)

  // Picking a swatch applies it at the swatch's own tint: 100, or a Recently used entry's tint.
  // The previous tint isn't carried over.
  function handleColorSelect(color: WireColor, colorTint = 100) {
    setInternalColorId(isNoneColor(color) ? null : color.colorId)
    setTint(colorTint)
    setTintMixed(false)
    tintTypedRef.current = false

    if (!isNoneColor(color)) {
      addRecentColor(color.colorId, colorTint)
    }

    onColorSelect?.(color, colorTint, target)
  }

  // Starting a drag marks the swatch selected right away; the drop itself applies the color.
  function handleColorDragStart(color: WireColor, colorTint = 100) {
    setInternalColorId(isNoneColor(color) ? null : color.colorId)
    setTint(colorTint)
    setTintMixed(false)
  }

  function handleTintChange(v: number) {
    setTint(v)
    setTintMixed(false)
    onTintChange?.(v)
  }

  // A committed tint is one undo step (the host batches it) and joins Recently used.
  function handleTintCommit(v: number) {
    tintTypedRef.current = false
    onTintCommit?.(v)

    if (internalColor != null) addRecentColor(internalColor.colorId, v)
  }

  const isTintDisabled = internalColor == null

  const title = target === 'text' ? 'Text' : target === 'fill' ? 'Fill' : 'Stroke'

  return (
    <div ref={popoverRootRef} className="flex flex-col w-80">
      <div className="flex flex-col gap-2 px-3 pt-3 pb-2 border-b border-border shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 min-w-0">
            {onDragStart && (
              <button
                type="button"
                aria-label={`Move ${title} color picker`}
                onPointerDown={onDragStart}
                onPointerMove={onDragMove}
                onPointerUp={onDragEnd}
                onPointerCancel={onDragEnd}
                className={cn(
                  'touch-none flex h-6 w-6 shrink-0 items-center justify-center border-none bg-transparent p-0 text-muted-foreground',
                  isDragging ? 'cursor-grabbing' : 'cursor-grab'
                )}
              >
                <GripVertical className="size-6" strokeWidth={2} aria-hidden="true" />
              </button>
            )}
            <span className="text-sm font-medium text-foreground leading-5">{title}</span>
          </div>
          <button
            type="button"
            aria-label="Close color picker"
            className="flex items-center justify-center size-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={onClose}
          >
            <X className="size-4" />
          </button>
        </div>

        {target !== 'text' && (
          <Tabs defaultValue="solid">
            <TabsList className="w-full">
              <TabsTrigger value="solid" className="flex-1">
                Solid
              </TabsTrigger>
              <TabsTrigger value="gradient" className="flex-1" disabled>
                Gradient
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <div className="flex items-center gap-3 w-full">
          <div
            className="flex items-center justify-center size-8 p-1 rounded-md ring-2 ring-inset ring-muted bg-popover shrink-0 overflow-hidden cursor-grab active:cursor-grabbing"
            onPointerDown={headerSwatchDrag.onPointerDown}
            onPointerMove={headerSwatchDrag.onPointerMove}
            onPointerUp={headerSwatchDrag.onPointerUp}
            onPointerCancel={headerSwatchDrag.onPointerCancel}
          >
            {internalColor == null ? (
              <img src={colorNoneSvg} alt="None" className="size-6 block" draggable={false} />
            ) : (
              <span
                className="block size-6 rounded-[2px] border border-foreground shrink-0"
                style={{
                  backgroundColor: tintedPaletteHex(internalColor, tint) ?? internalColor.hexValue,
                }}
              />
            )}
          </div>
          <span className="text-sm font-medium text-foreground whitespace-nowrap shrink-0">
            {internalColor == null ? 'None' : internalColor.colorName}
          </span>
          <span className="text-xs text-muted-foreground leading-none shrink-0">Tint</span>
          <Slider
            value={[tint]}
            onValueChange={([v]) => handleTintChange(v)}
            onValueCommit={([v]) => handleTintCommit(v)}
            min={0}
            max={100}
            step={1}
            thumbLabel="Tint"
            disabled={isTintDisabled}
            className="flex-1 min-w-0"
          />
          <div className="w-12 shrink-0">
            <Input
              size="xs"
              aria-label="Tint percentage"
              value={tintMixed ? '' : `${tint}%`}
              placeholder={tintMixed ? 'Mixed' : undefined}
              disabled={isTintDisabled}
              onChange={(e) => {
                const parsed = parseInt(e.target.value.replace('%', ''), 10)

                if (!isNaN(parsed)) {
                  tintTypedRef.current = true
                  handleTintChange(Math.min(100, Math.max(0, parsed)))
                }
              }}
              // Typed digits only preview; Enter blurs, and the blur commits once.
              onBlur={() => {
                if (tintTypedRef.current) handleTintCommit(tint)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  e.currentTarget.blur()
                }
              }}
              className="text-center px-1"
            />
          </div>
        </div>

        <InputGroup>
          <InputGroupPrefix>
            <Search className="size-4 text-muted-foreground" />
          </InputGroupPrefix>
          <Input
            size="sm"
            placeholder="Search by formula color"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </InputGroup>
      </div>

      {searchResults != null ? (
        <div className="flex flex-col px-3 pt-2 pb-3 gap-2 shrink-0">
          <p className="text-xs font-medium text-muted-foreground whitespace-nowrap">
            {searchResults.length > 0
              ? `${searchResults.length} results for "${searchQuery.trim()}"`
              : `No results for "${searchQuery.trim()}"`}
          </p>
          {searchResults.length > 0 ? (
            <>
              <ColorPopoverToolbar
                colorFamily={colorFamily}
                onColorFamilyChange={setColorFamily}
                colorLayout={colorLayout}
                onColorLayoutChange={setColorLayout}
              />
              <ColorPopoverColorList
                colors={searchResults}
                colorLayout={colorLayout}
                internalColorId={internalColorId}
                styleColorIds={styleColorIds}
                listRef={listRef}
                listHeight={renderedListHeight}
                onColorSelect={handleColorSelect}
                onColorDragStart={handleColorDragStart}
              />
              <ColorPopoverResizeGrip
                isResizing={isResizing}
                onPointerDown={handleResizeStart}
                onPointerMove={handleResizeMove}
                onPointerUp={handleResizeEnd}
                onPointerCancel={handleResizeEnd}
              />
            </>
          ) : (
            <p className="text-xs font-medium text-muted-foreground">
              Check the formula number, or clear the search to see all colors.
            </p>
          )}
        </div>
      ) : (
        <>
          {/* px-4 gives 288px inner width — fits 9 × 32px swatches with no column gap */}
          <div className="flex flex-col gap-2 px-4 py-2 shrink-0">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground min-w-0">
                  {selectedStyle != null ? (
                    <>
                      <span className="truncate max-w-[104px] leading-none">
                        {selectedStyle.displayName}
                      </span>
                      <span className="shrink-0 leading-none">· {selectedStyle.colors.length}</span>
                    </>
                  ) : pages.length > 0 ? (
                    <span className="leading-none">No style assigned</span>
                  ) : null}
                </div>

                {pages.length > 0 && (
                  <div className="flex items-center gap-0.5 shrink-0 ml-2">
                    {pages.map((page) => (
                      <Button
                        key={page.number}
                        variant={page.number === effectivePageNumber ? 'outline' : 'ghost'}
                        size="sm"
                        onClick={() => setSelectedPageNumber(page.number)}
                      >
                        Page {page.number}
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              {selectedStyle != null && selectedStyle.colors.length > 0 && (
                <div className="flex flex-wrap gap-y-1.5">
                  {selectedStyle.colors.map((color) => (
                    <ColorSwatch
                      key={color.colorId}
                      color={color}
                      isSelected={color.colorId === internalColorId}
                      onClick={handleColorSelect}
                      onDragStart={handleColorDragStart}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-start">
              <div className="flex shrink-0 pt-5">
                <ColorSwatch
                  color={NONE_COLOR}
                  isSelected={internalColorId === null}
                  onDragStart={handleColorDragStart}
                  onClick={() => handleColorSelect(NONE_COLOR)}
                />
                {whiteColor != null && (
                  <ColorSwatch
                    color={whiteColor}
                    isSelected={whiteColor.colorId === internalColorId}
                    onClick={handleColorSelect}
                    onDragStart={handleColorDragStart}
                  />
                )}
                {blackColor != null && (
                  <ColorSwatch
                    color={blackColor}
                    isSelected={blackColor.colorId === internalColorId}
                    onClick={handleColorSelect}
                    onDragStart={handleColorDragStart}
                  />
                )}
              </div>

              <div className="relative w-8 h-[52px] shrink-0">
                <div className="absolute left-4 top-6 h-6 w-px bg-border" />
              </div>

              <div className="flex flex-col gap-1.5 flex-1">
                <span className="text-xs font-medium text-muted-foreground leading-4">
                  Recently used
                </span>

                {recentColors.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    {recentRow1.length > 0 && (
                      <div className="flex">
                        {recentRow1.map((recent) => (
                          <ColorSwatch
                            key={`${recent.color.colorId}:${recent.tint}`}
                            color={recent.color}
                            tint={recent.tint}
                            isSelected={isRecentSelected(recent)}
                            onClick={handleColorSelect}
                            onDragStart={handleColorDragStart}
                          />
                        ))}
                      </div>
                    )}
                    {recentRow2.length > 0 && (
                      <div className="flex">
                        {recentRow2.map((recent) => (
                          <ColorSwatch
                            key={`${recent.color.colorId}:${recent.tint}`}
                            color={recent.color}
                            tint={recent.tint}
                            isSelected={isRecentSelected(recent)}
                            onClick={handleColorSelect}
                            onDragStart={handleColorDragStart}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={cn('flex flex-col px-1 shrink-0', !allColorsOpen && 'pb-3')}>
            <button
              type="button"
              className="flex items-center gap-2 h-9 w-full px-2 rounded-sm hover:bg-muted"
              onClick={() => setAllColorsOpen((prev) => !prev)}
              aria-expanded={allColorsOpen}
            >
              <span className="text-sm font-medium text-foreground leading-none">All colors</span>
              <span className="flex-1 text-xs text-muted-foreground text-left leading-none">
                {allColors.length}
              </span>
              <ChevronDown
                className={cn(
                  'size-4 transition-transform',
                  allColorsOpen ? 'rotate-180 text-foreground' : 'text-muted-foreground'
                )}
              />
            </button>

            {allColorsOpen && (
              <>
                <ColorPopoverToolbar
                  colorFamily={colorFamily}
                  onColorFamilyChange={setColorFamily}
                  colorLayout={colorLayout}
                  onColorLayoutChange={setColorLayout}
                />
                <ColorPopoverColorList
                  colors={sortedAllColors}
                  colorLayout={colorLayout}
                  internalColorId={internalColorId}
                  styleColorIds={styleColorIds}
                  listRef={listRef}
                  listHeight={renderedListHeight}
                  onColorSelect={handleColorSelect}
                  onColorDragStart={handleColorDragStart}
                />
                <ColorPopoverResizeGrip
                  isResizing={isResizing}
                  onPointerDown={handleResizeStart}
                  onPointerMove={handleResizeMove}
                  onPointerUp={handleResizeEnd}
                  onPointerCancel={handleResizeEnd}
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
